import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowUpRight, Play, Download, Clock, Shield } from 'lucide-react';
import { PHOTOS, STAN_STRATEGY_URL, SPEAKING_TOPICS, PARTNERS_PAGE, LOGO_URL, COMPANIES } from '@/constants/content';
import { PARTNERS } from '@/constants/testIds';
import { submitContact } from '@/lib/api';
import { usePageMeta } from '@/lib/usePageMeta';
import { SEO } from '@/constants/seo';

export default function Partners() {
  usePageMeta({ ...SEO.partners });
  const p = PARTNERS_PAGE;

  const [form, setForm] = useState({ name: '', email: '', organization: '', message: '' });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact({
        kind: 'partnership',
        name: form.name,
        email: form.email,
        organization: form.organization,
        subject: 'Executive Portal · Event Inquiry',
        message: form.message,
      });
      toast.success('Thank you. The team will be in touch within 48 hours.');
      setForm({ name: '', email: '', organization: '', message: '' });
    } catch (err) {
      toast.error('Could not submit. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-matte text-ivory" data-testid={PARTNERS.root}>
      {/* HERO */}
      <section className="relative pt-40 lg:pt-56 pb-24 lg:pb-40 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-30 noise-grain" aria-hidden />
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/40 via-transparent to-matte" aria-hidden />
        <div className="relative mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <div className="flex items-center gap-4 mb-10">
            <Shield size={16} strokeWidth={1.3} className="text-gold" />
            <span className="font-label text-[11px] tracking-widest uppercase text-gold">Private Executive Portal · By Invitation</span>
          </div>
          <div className="grid lg:grid-cols-12 gap-16 items-end">
            <div className="lg:col-span-8">
              <h1 className="font-serif-display text-6xl md:text-7xl lg:text-[7.5rem] leading-[0.94] tracking-tightest">
                For the teams
                <br />
                bringing Toia
                <br />
                <em className="italic text-gold">to the room.</em>
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-ivory/70 leading-relaxed text-[15px] max-w-md">
                A resource center curated for conference organizers, corporate event planners,
                podcast hosts, media professionals, and strategic partners. Everything you need
                to move quickly, in one quiet room.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/speaking#inquiry"
                  data-testid={PARTNERS.ctaSpeaking}
                  className="btn-secondary border-ivory text-ivory hover:bg-ivory hover:text-matte"
                >
                  Book Toia Wallace
                  <ArrowUpRight size={14} strokeWidth={1.6} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SPEAKER REEL */}
      <section className="pb-24 lg:pb-32">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <div className="relative rounded-3xl overflow-hidden">
            <img src={PHOTOS.speaking} alt="Toia Wallace speaker reel" className="w-full h-[60vh] lg:h-[85vh] object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-matte/40" aria-hidden />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-8 text-center px-6">
              <div className="eyebrow text-gold">Speaker Reel</div>
              <button
                type="button"
                className="group inline-flex items-center gap-4 rounded-full bg-ivory/10 backdrop-blur border border-ivory/30 hover:border-gold px-6 py-6 md:px-10 md:py-8 transition-colors duration-500"
                aria-label="Play speaker reel, coming soon"
                disabled
              >
                <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gold grid place-items-center transition-transform duration-500 group-hover:scale-105">
                  <Play size={26} strokeWidth={1.4} className="text-matte translate-x-0.5" />
                </span>
                <span className="text-left">
                  <span className="block font-serif-display text-2xl md:text-4xl leading-tight tracking-tight">Watch the reel</span>
                  <span className="font-label text-[10px] tracking-widest uppercase text-ivory/70">Coming soon · 90-second edit</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* SPEAKING TOPICS */}
      <section className="py-16 lg:py-24 bg-[#0d0d0d]">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="eyebrow text-gold mb-6">01 · Topics</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
              Signature keynotes.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-x-8">
              {SPEAKING_TOPICS.map((t, i) => (
                <li key={t} className="border-t border-ivory/15 py-6 flex items-baseline gap-6">
                  <span className="font-label text-[11px] tracking-widest uppercase text-gold">{String(i + 1).padStart(2, '0')}</span>
                  <span className="font-serif-display text-2xl md:text-3xl text-ivory tracking-tight leading-snug">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* AUDIENCE TAKEAWAYS */}
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="eyebrow text-gold mb-6">02 · Audience Takeaways</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
              What the room leaves with.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-1">
            {p.audienceTakeaways.map((t, i) => (
              <div key={i} className="border-t border-ivory/15 py-6 flex items-baseline gap-6">
                <span className="font-serif-display text-3xl md:text-4xl text-taupe/70 tracking-tighter">{String(i + 1).padStart(2, '0')}</span>
                <p className="font-serif-display text-xl md:text-2xl text-ivory tracking-tight leading-snug">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BIO */}
      <section className="py-16 lg:py-24 bg-[#0d0d0d]">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5">
            <img src={PHOTOS.media} alt="Toia Wallace headshot" className="rounded-3xl w-full h-[60vh] object-cover" loading="lazy" />
          </div>
          <div className="lg:col-span-7">
            <div className="eyebrow text-gold mb-6">03 · Professional Biography</div>
            <div className="mb-10">
              <div className="eyebrow text-ivory/60 mb-3">Short</div>
              <p className="font-serif-display text-2xl md:text-3xl leading-snug tracking-tight text-ivory">{p.bio.short}</p>
            </div>
            <div>
              <div className="eyebrow text-ivory/60 mb-3">Long</div>
              <p className="text-ivory/75 leading-relaxed max-w-2xl">{p.bio.long}</p>
            </div>
          </div>
        </div>
      </section>

      {/* COMPANY OVERVIEW */}
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <div className="eyebrow text-gold mb-6">04 · The Companies</div>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-16 max-w-2xl">
            The ecosystem at a glance.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {COMPANIES.slice(0, 8).map((c, i) => (
              <article key={c.slug} className="bg-[#0d0d0d] rounded-2xl p-6 border border-ivory/10">
                <div className="eyebrow text-gold mb-3">{String(i + 1).padStart(2, '0')}</div>
                <h3 className="font-serif-display text-xl md:text-2xl text-ivory tracking-tight leading-tight mb-2">{c.name}</h3>
                <p className="font-serif-display italic text-taupe/80 text-sm leading-snug">{c.tagline}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section className="py-16 lg:py-24 bg-[#0d0d0d]">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <div className="eyebrow text-gold mb-6">05 · AI Certifications</div>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-16 max-w-2xl">
            Credentials on the record.
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {p.certifications.map((c, i) => (
              <article key={c.name} className="p-8 border border-ivory/10 rounded-2xl hover:border-gold/40 transition-colors duration-500">
                <div className="eyebrow text-gold mb-4">{String(i + 1).padStart(2, '0')}</div>
                <h3 className="font-serif-display text-2xl text-ivory tracking-tight leading-tight mb-3">{c.name}</h3>
                <p className="text-sm text-ivory/70 leading-relaxed">{c.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* BRAND ASSETS + MEDIA KIT */}
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <div>
              <div className="eyebrow text-gold mb-6">06 · Brand Assets & Press Kit</div>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] max-w-2xl">
                Press kit for producers.
              </h2>
            </div>
            <p className="text-ivory/60 max-w-md">
              Final assets are in production. Each item is architected to receive its file
              without redesigning this portal.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {p.brandAssets.map((d, i) => (
              <div key={d.key} className="group relative bg-[#0d0d0d] rounded-2xl p-8 border border-ivory/10 hover:border-gold/40 transition-colors duration-500">
                <div className="flex items-start justify-between mb-8">
                  <div className="eyebrow text-gold">{String(i + 1).padStart(2, '0')}</div>
                  <div className="inline-flex items-center gap-2 text-ivory/50">
                    <Clock size={14} strokeWidth={1.4} />
                    <span className="font-label text-[10px] tracking-widest uppercase">Coming Soon</span>
                  </div>
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl tracking-tight text-ivory leading-tight mb-3">
                  {d.label}
                </h3>
                <p className="text-sm text-ivory/50">{d.hint}</p>
                <button type="button" disabled className="mt-8 inline-flex items-center gap-2 text-ivory/40 font-label text-[10px] tracking-widest uppercase cursor-not-allowed">
                  <Download size={14} strokeWidth={1.4} />
                  Download · Available soon
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EVENT INQUIRY FORM */}
      <section className="py-16 lg:py-24 bg-[#0d0d0d]">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="eyebrow text-gold mb-6">07 · Event & Partnership Inquiry</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
              Start the conversation.
            </h2>
            <p className="mt-8 text-ivory/70 leading-relaxed max-w-md">
              For custom engagements, sponsorships, and strategic collaborations. For direct
              speaking bookings with confirmed dates, use the Book Toia Wallace button.
            </p>
            <div className="mt-10 flex flex-col gap-3">
              <Link
                to="/speaking#inquiry"
                className="btn-secondary border-ivory text-ivory hover:bg-ivory hover:text-matte self-start"
              >
                Book Toia Wallace
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </Link>
            </div>
          </div>
          <form onSubmit={submit} data-testid={PARTNERS.inquiryForm} className="lg:col-span-7 grid sm:grid-cols-2 gap-x-8 gap-y-2">
            <PartnerField label="Name" required value={form.name} onChange={set('name')} testId={PARTNERS.inquiryName} />
            <PartnerField label="Email" type="email" required value={form.email} onChange={set('email')} testId={PARTNERS.inquiryEmail} />
            <div className="sm:col-span-2">
              <PartnerField label="Organization" required value={form.organization} onChange={set('organization')} testId={PARTNERS.inquiryOrg} />
            </div>
            <div className="sm:col-span-2">
              <label className="block eyebrow text-gold mb-3 mt-6">Message</label>
              <textarea
                rows={5}
                required
                value={form.message}
                onChange={set('message')}
                data-testid={PARTNERS.inquiryMessage}
                className="w-full bg-transparent border-b border-ivory/25 py-3 text-ivory focus:outline-none focus:border-gold resize-none"
              />
            </div>
            <div className="sm:col-span-2 mt-8">
              <button type="submit" disabled={loading} data-testid={PARTNERS.inquirySubmit} className="btn-primary bg-gold text-matte hover:bg-[#b8944e] disabled:opacity-50">
                {loading ? 'Sending…' : 'Submit Inquiry'}
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* CLOSING */}
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <img src={LOGO_URL} alt="Toia Wallace" className="h-16 w-auto mx-auto brightness-0 invert opacity-90" />
          <div className="mt-8 hairline-gold h-px w-16 mx-auto" />
          <p className="mt-10 font-serif-display italic text-2xl md:text-3xl leading-snug tracking-tight text-ivory/85">
            &ldquo;Every stage is a room. Every partnership is a room. I only walk into rooms that
            deserve the work.&rdquo;
          </p>
        </div>
      </section>
    </div>
  );
}

function PartnerField({ label, testId, ...props }) {
  return (
    <label className="block">
      <span className="block eyebrow text-gold mb-3 mt-6">{label}</span>
      <input
        {...props}
        data-testid={testId}
        className="w-full bg-transparent border-b border-ivory/25 py-3 text-ivory focus:outline-none focus:border-gold font-serif-display text-xl"
      />
    </label>
  );
}
